import React, { useState, useEffect } from 'react';
import { Calculator, Sparkles, RefreshCw, Copy, CheckCircle2, ShieldCheck } from 'lucide-react';
import GphRichBlock from '../GphRichBlock';
import { auth } from '../../../firebase';
import { API_URL } from '../../../config';

export default function CalculatorSkillAdapter({ agent, context = {} }) {
  const game = context.game || agent.host.canonicalGame || 'pubg_mobile';
  const isFreeFire = game === 'free_fire';
  const gameLabel = isFreeFire ? 'Free Fire' : 'PUBG Mobile';

  const [baseValue, setBaseValue] = useState(context.baseValue || context.extractedEntities?.number || '215');
  const [weaponId, setWeaponId] = useState(context.weapon || 'm416_general');
  const [mainScope, setMainScope] = useState(context.scope || 'red_dot');
  const [controlMode, setControlMode] = useState(context.controlMode || 'gyro');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState(null);
  const [copied, setCopied] = useState(false);

  const calculateResult = async () => {
    const val = parseFloat(String(baseValue).replace(/,/g, '.'));
    if (!Number.isFinite(val) || val <= 0) {
      setError('Insira um valor base válido para calcular.');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const token = await auth.currentUser?.getIdToken();
      if (!isFreeFire) {
        const res = await fetch(`${API_URL}/pubg-calculator/analyze`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({
            weapon: weaponId,
            mainScope,
            knownPerfectValue: val,
            controlMode,
            requestId: Math.random().toString(36).substring(2, 15),
            language: 'pt',
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data?.rows) {
          setRows(data.rows);
        } else {
          const ratios = { red_dot: 1.0, '2x': 0.88, '3x': 0.72, '4x': 0.60, '6x': 0.45, '8x': 0.35 };
          const baseRatio = ratios[mainScope] || 1.0;
          const fallbackRows = [
            { scopeLabel: 'Red Dot / Holográfica', value: Math.round(val * (ratios.red_dot / baseRatio)) },
            { scopeLabel: 'Mira 2x', value: Math.round(val * (ratios['2x'] / baseRatio)) },
            { scopeLabel: 'Mira 3x', value: Math.round(val * (ratios['3x'] / baseRatio)) },
            { scopeLabel: 'Mira 4x (ACOG)', value: Math.round(val * (ratios['4x'] / baseRatio)) },
            { scopeLabel: 'Mira 6x', value: Math.round(val * (ratios['6x'] / baseRatio)) },
            { scopeLabel: 'Mira 8x Sniper', value: Math.round(val * (ratios['8x'] / baseRatio)) },
          ];
          setRows(fallbackRows);
        }
      } else {
        const res = await fetch(`${API_URL}/freefire-calculator/calculate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({
            input: { dpi: 400, fov: 90, sensitivity: val, currentSens: { geral: val } },
            idempotencyKey: Math.random().toString(36).substring(2, 15)
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data?.response?.table) {
          setRows(data.response.table);
        } else {
          setRows([
            { scopeLabel: 'Geral', value: Math.round(val) },
            { scopeLabel: 'Ponto Vermelho', value: Math.round(val * 0.95) },
            { scopeLabel: 'Mira 2x', value: Math.round(val * 0.88) },
            { scopeLabel: 'Mira 4x', value: Math.round(val * 0.78) },
            { scopeLabel: 'Mira AWM / Sniper', value: Math.round(val * 0.65) },
            { scopeLabel: 'Olhadinha', value: Math.round(val * 0.70) }
          ]);
        }
      }
    } catch (err) {
      setError('Não foi possível conectar à engine. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (context.baseValue) {
      calculateResult();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.baseValue]);

  const handleCopy = () => {
    if (!rows) return;
    const text = rows.map(r => `${r.scopeLabel || r.scope_label || r.scope}: ${r.value}`).join('\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <GphRichBlock
      eyebrow="Cálculo de miras"
      title="SUAS MIRAS CALCULADAS"
      subtitle={`Valores calibrados a partir da sua mira base em ${gameLabel}.`}
    >
      <div className="space-y-6 pt-2 text-left">
        {/* Input panel */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold tracking-widest text-lime-400 uppercase flex items-center gap-1.5">
              <Calculator size={14} />
              Mira Base Calibrada
            </span>
            <span className="text-[10px] text-slate-500">Valor usado no treino</span>
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            <input
              type="number"
              value={baseValue}
              onChange={(e) => setBaseValue(e.target.value)}
              placeholder="Ex: 215"
              className="bg-slate-900 border border-white/5 rounded-lg px-3 py-2 text-white font-mono text-sm w-28 focus:outline-none focus:border-lime-500"
            />
            {!isFreeFire && (
              <select
                value={mainScope}
                onChange={(e) => setMainScope(e.target.value)}
                className="bg-slate-900 border border-white/5 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-lime-500"
              >
                <option value="red_dot">Red Dot / Holo</option>
                <option value="2x">Mira 2x</option>
                <option value="3x">Mira 3x</option>
              </select>
            )}
            <button
              type="button"
              onClick={calculateResult}
              disabled={loading}
              className="gph-btn gph-btn--primary text-xs py-2 px-4 flex items-center gap-1.5 ml-auto"
            >
              {loading ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {loading ? 'Calculando...' : 'Calcular'}
            </button>
          </div>
          {error && <p className="text-xs text-rose-400 m-0">{error}</p>}
        </div>

        {/* Tactical results list */}
        {rows && (
          <div className="space-y-3">
            <div className="flex items-center justify-between border-b border-white/5 pb-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Curva de Sensibilidade Calculada</span>
              <button
                type="button"
                onClick={handleCopy}
                className="text-[11px] font-mono text-black font-extrabold bg-lime-400 hover:bg-lime-300 active:scale-95 transition-all flex items-center gap-1.5 px-3 py-1.5 rounded-lg cursor-pointer"
              >
                {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
                {copied ? 'Copiado!' : 'Copiar'}
              </button>
            </div>
            
            <div className="divide-y divide-white/5">
              {rows.map((row, idx) => (
                <div key={idx} className="flex items-center justify-between py-3 text-xs font-mono">
                  <span className="text-slate-300 font-sans">{row.scopeLabel || row.scope_label || row.scope || `Mira ${idx+1}`}</span>
                  <span className="text-lime-400 font-bold bg-lime-500/10 border border-lime-500/20 px-3 py-1 rounded-lg text-sm">
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer actions */}
        <div className="border-t border-white/5 pt-5 mt-6 flex items-center justify-between">
          <span className="text-[11px] text-slate-500 flex items-center gap-1.5">
            <ShieldCheck size={14} className="text-lime-400" /> Engine matemática sincronizada
          </span>
          <button
            type="button"
            className="gph-btn gph-btn--ghost text-xs"
            onClick={() => agent.runCapability('sensitivity_diagnostic', {})}
          >
            Ajustar outro sintoma
          </button>
        </div>
      </div>
    </GphRichBlock>
  );
}
